import { mapSearchDataForFetch } from '@aytm/app/stats/page/comments/lib/map'
import { DEFAULT_ORDER_DIRECTION, kudosText } from '@aytm/app/stats/page/comments/lib/constants'
import { cleanSearchData, getDottedQuestionName } from '@aytm/app/stats/page/comments/lib/utils'

import { sendJSONData, sendRawJSONData, sendJobJSONData } from '@aytm/app/stats/lib/io/fetch'
import { addCSRF } from '@aytm/app/stats/lib/util/helpers'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'
import { DEFAULT_ORDER_BY, DEFAULT_SELECTED_QUESTION } from '@aytm/app/stats/page/components/lib/constants'

let apiKey = ''

export const setApiKey = key => (apiKey = (key != undefined && null !== key && key.code) || '')

// Read from the boundary rather than handed in: these are stamped once and every caller was
// deriving the same two strings from the same globals, one of them through a prop the legacy shell
// had to thread down. Functions rather than constants so a fabricated page is still followed.
const statUrl = () => PageGlobals.STAT_SERVER_URL
const statCsUrl = () => `${PageGlobals.STAT_SERVER_URL}/cs`
const statActionsUrl = () => `${PageGlobals.AYTM_DOMAIN}/surveys/${PageGlobals.SURVEY_ID}/cs/:responseId`

export const fetchJSONData = async ({ url, method, data, signal }) => {
  if (null === apiKey || !apiKey.length) {
    // throw new Error('Api Key is invalid')
    return { status: 'INVALID KEY' }
  }

  return sendJSONData({
    url: `${url}?key=${apiKey}`,
    method,
    data,
    signal
  })
}

export const fetchSearchResults = async ({
  query,
  selectedQuestion,
  pageSize,
  orderBy,
  orderDirection = 'asc',
  translation,
  currentPage,
  isQAMode = false,
  nocache = false,
  includeSchema,
  flattenAnswers = null,
  uidsToApprove = null,
  signal
}) => {
  const request = {
    query,
    selectedQuestion: selectedQuestion,
    pageSize,
    orderBy,
    orderDirection,
    translation,
    currentPage,
    isQAMode,
    nocache,
    includeSchema,
    flattenAnswers,
    uidsToApprove
  }

  return sendJSONData({
    url: `${statCsUrl()}?key=${apiKey}`,
    method: 'POST',
    data: mapSearchDataForFetch(request),
    signal
  })
}

export const approve = async ({ responseId, signal }) =>
  fetchJSONData({
    url: `${statCsUrl()}/${responseId}/approve`,
    method: 'POST',
    data: {},
    signal
  })

export const reject = async ({ responseId, authenticity_token = null, message = null, signal }) =>
  sendRawJSONData({
    url: `${statActionsUrl().replace(':responseId', responseId)}/reject`,
    method: 'POST',
    data: {
      message,
      authenticity_token
    },
    signal
  })

export const toggleKudos = async ({ responseId, authenticity_token = null, message = null, type = 'kudos', signal }) =>
  sendRawJSONData({
    url: `${statActionsUrl().replace(':responseId', responseId)}/${type}`,
    method: 'POST',
    data: {
      message: message || kudosText,
      authenticity_token
    },
    signal
  })

export const tagsList = async ({ query, selectedQuestion, pageSize, currentPage, signal }) => {
  const search =
    null !== query
      ? Object.assign(
          {},
          null !== query ? { query } : {},
          selectedQuestion !== DEFAULT_SELECTED_QUESTION
            ? { scope: selectedQuestion.replace(/(\d)(\w)/gi, '$1.$2') }
            : {}
        )
      : null
  const data = Object.assign(
    {
      action: 'list',
      page: currentPage,
      pageSize
    },
    Object.keys(search).length ? { search } : void 0
  )
  return fetchJSONData({
    url: `${statCsUrl()}/tags`,
    method: 'POST',
    data,
    signal
  })
}

export const setCodebook = async ({
  codebook,
  path = '*',
  request = {},
  responseId = '*',
  action = 'create',
  signal
}) => {
  const search = {
    order: request.orderBy || DEFAULT_ORDER_BY,
    direction: request.orderDirection || DEFAULT_ORDER_DIRECTION,
    query: cleanSearchData(request.query) || []
  }
  if (request.selectedQuestion && request.selectedQuestion !== DEFAULT_SELECTED_QUESTION) {
    search.scope = getDottedQuestionName(request.selectedQuestion)
  }

  const data = {
    codebook: codebook.codebook,
    type: codebook.type,
    path,
    search,
    responseId
  }
  return fetchJSONData({
    url: `${statCsUrl()}/codebooks/${action}`,
    method: 'POST',
    data,
    signal
  })
}

export const setSurveyCodebook = async ({ codebook, newValue = null, action = 'add', signal }) => {
  const data = {
    codebook: codebook.codebook,
    type: codebook.type,
    tags: codebook.tags,
    count: codebook.count
  }
  if (null !== newValue) {
    data.update = newValue
  }
  return fetchJSONData({
    url: `${statUrl()}/codebooks/${action}`,
    method: 'POST',
    data,
    signal
  })
}

export const setTag = async ({ tag, path = '*', request = {}, responseId = '*', action = 'create', signal }) => {
  const search = {
    query: cleanSearchData(request.query) || [],
    order: request.orderBy || DEFAULT_ORDER_BY,
    direction: request.orderDirection || DEFAULT_ORDER_DIRECTION
  }
  if (request.selectedQuestion && request.selectedQuestion !== DEFAULT_SELECTED_QUESTION) {
    search.scope = getDottedQuestionName(request.selectedQuestion)
  }

  const data = {
    tag: tag.codebook == undefined || null === tag.codebook || 'sentiment' == tag.type ? tag.tag : tag.label,
    type: tag.type,
    path,
    search,
    responseId
  }
  if ('sentiment' != tag.type) {
    data.codebook = tag.codebook || null
  }
  return fetchJSONData({
    url: `${statCsUrl()}/tags/${action}`,
    method: 'POST',
    data,
    signal
  })
}

export const setResponseTag = async ({ tag, path, responseId, action = 'create', signal }) => {
  const data = {
    tag: tag.codebook == undefined || null === tag.codebook || 'sentiment' == tag.type ? tag.tag : tag.label,
    type: tag.type,
    path
  }
  if ('sentiment' != tag.type) {
    data.codebook = tag.codebook || null
  }
  return fetchJSONData({
    url: `${statCsUrl()}/${responseId}/tags/${action}`,
    method: 'POST',
    data,
    signal
  })
}

export const setSurveyTag = async ({ tag, newValue = null, action = 'add', signal }) => {
  const data = {
    tag: tag.codebook == undefined || null === tag.codebook || 'sentiment' == tag.type ? tag.tag : tag.label,
    type: tag.type,
    count: tag.amount || 0
  }
  if ('sentiment' != tag.type) {
    data.codebook = tag.codebook || null
  }
  if (null !== newValue) {
    data.update = newValue
  }
  if (Object.hasOwn(tag, 'destination') && null !== tag.destination) {
    data.destination = tag.destination.codebook || null
  }
  if ('merge' == action) {
    data.merge = true
  }
  return fetchJSONData({
    url: `${statUrl()}/tags/${'merge' == action ? (tag.update && 'update') || 'clone' : action}`,
    method: 'POST',
    data,
    signal
  })
}

export const setSurveyTags = async ({ tags, codebook, action = 'create', signal }) => {
  const data = {
    tags: tags.map(item => item.label),
    type: codebook.type,
    codebook: codebook.codebook || null,
    count: 0
  }
  return fetchJSONData({
    url: `${statUrl()}/tags/${action}`,
    method: 'POST',
    data,
    signal
  })
}

export const runSentiments = async ({ job = null, signal }) => {
  if (null !== job) {
    return fetchJSONData({
      url: `${statCsUrl()}/job`,
      method: 'POST',
      data: { key: apiKey, token: job.token },
      signal
    })
  } else {
    const data = { command: 'ai.oeSentiments' }
    return sendJobJSONData({
      url: PageGlobals.STAT_JOB_API_URL,
      method: 'POST',
      data: addCSRF(data),
      signal
    })
  }
}

export const runSurveySkipper = async ({ job = null, codebook, selectedQuestion, guided = false, signal }) => {
  if (null !== job) {
    return fetchJSONData({
      url: `${statCsUrl()}/job`,
      method: 'POST',
      data: { key: apiKey, token: job.token },
      signal
    })
  } else {
    const data = { command: 'ai.oeCoding' }
    if (codebook) {
      data.codebook = {
        title: codebook.codebook,
        type: codebook.type,
        tags: codebook.tags
      }
    }
    if (selectedQuestion && selectedQuestion !== DEFAULT_SELECTED_QUESTION) {
      data.include = getDottedQuestionName(selectedQuestion)
    }
    if (guided) data.guided = true
    return sendJobJSONData({
      url: PageGlobals.STAT_JOB_API_URL,
      method: 'POST',
      data: addCSRF(data),
      signal
    })
  }
}

export const runSurveyTranslation = async ({ job = null, translationLanguage, signal }) => {
  if (null !== job) {
    return fetchJSONData({
      url: `${statCsUrl()}/job`,
      method: 'POST',
      data: { key: apiKey, token: job.token },
      signal
    })
  } else {
    const data = { command: 'ai.oeTranslate', lang: translationLanguage }
    return sendJobJSONData({
      url: PageGlobals.STAT_JOB_API_URL,
      method: 'POST',
      data: addCSRF(data),
      signal
    })
  }
}
